import React from 'react';
import { MenuItem, TextField } from '@mui/material';
import { scheduleText } from 'components/pipelines/schedule';

import { useTranslation } from 'react-i18next';

export const CUSTOM_PRESET = '__custom__';

const PRESET_CRONS = ['0 * * * *', '0 */6 * * *', '0 2 * * *', '0 3 * * 1'];

// Values are crons on the schedule's wall clock (`zone`, UTC for a new
// pipeline); labels are worded in the viewer's local time, so a UTC
// "0 2 * * *" reads as 06:00 at UTC+4.
function presets(zone) {
  return PRESET_CRONS.map((value) => {
    const text = scheduleText(value, new Date(), zone);
    return { label: text.charAt(0).toUpperCase() + text.slice(1), value };
  });
}

/** Map a stored cron string to the preset select value ('' = manual only). */
export function presetForCron(cron) {
  if (!cron) return '';
  return PRESET_CRONS.includes(cron) ? cron : CUSTOM_PRESET;
}

/**
 * Schedule preset select + custom cron input, shared by the create form and
 * the edit dialog so both offer the same presets and free-form cron.
 */
export default function ScheduleCronFields({
  preset,
  customCron,
  onPresetChange,
  onCustomCronChange,
  cronError,
  zone = 'UTC',
}) {
  const { t } = useTranslation();
  return (
    <>
      <TextField
        select
        label={t('data.schedule')}
        value={preset}
        onChange={(e) => onPresetChange(e.target.value)}
        fullWidth
        sx={{ mb: preset === CUSTOM_PRESET ? 1 : 2 }}
        helperText={t('data.timesAreShownInYourLocalTimezone')}
      >
        <MenuItem value="">{t('data.manualOnly')}</MenuItem>
        {presets(zone).map((p) => (
          <MenuItem key={p.value} value={p.value}>
            {p.label}
          </MenuItem>
        ))}
        <MenuItem value={CUSTOM_PRESET}>{t('data.customCronExpression')}</MenuItem>
      </TextField>

      {preset === CUSTOM_PRESET && (
        <TextField
          label={t('data.cronExpressionValue', { value1: zone })}
          value={customCron}
          onChange={(e) => onCustomCronChange(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
          placeholder="0 2 * * *"
          error={!!cronError}
          helperText={
            cronError ||
            (customCron && scheduleText(customCron, new Date(), zone) !== t('data.onACustomSchedule')
              ? t('data.yourLocalTimeValue', { value1: scheduleText(customCron, new Date(), zone) })
              : t('data.fiveFieldsInValueMinuteHourDayOfMonthMonthDay', { value1: zone }))
          }
          inputProps={{ style: { fontFamily: 'monospace' } }}
        />
      )}
    </>
  );
}
